import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";

const monthAgo = () => new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

export default function AuditLog() {
  const [f, setF] = useState({ q: "", resource: "all", action: "all", start: monthAgo(), end: today() });
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const params = { start: f.start, end: f.end, limit: 300 };
    if (f.q) params.q = f.q;
    if (f.resource !== "all") params.resource = f.resource;
    if (f.action !== "all") params.action = f.action;
    try { const { data } = await api.get("/audit-logs", { params }); setData(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/audit-logs", { params: { start: f.start, end: f.end, format: "csv", limit: 2000 }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "audit_log.csv"; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="audit-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div><p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Security</p><h1 className="mt-3 text-4xl font-bold tracking-tight">Audit history</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">Every sensitive change — who, what, when, previous and new values, and the reason. Entries cannot be edited or erased.</p></div>
        <Button variant="outline" onClick={exportCsv} className="rounded-full h-12 font-bold" data-testid="audit-export-btn"><DownloadSimple size={18} className="mr-2" /> Export CSV</Button>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 bg-white rounded-2xl border border-border p-4" data-testid="audit-filters">
        <div className="lg:col-span-2"><Label className="text-xs font-bold uppercase tracking-wider">Search</Label>
          <div className="relative mt-1"><MagnifyingGlass size={16} className="absolute left-3 top-3 text-muted-foreground" /><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Summary, user, reason or record ID" className="h-10 rounded-xl pl-9" data-testid="audit-search" /></div></div>
        <div><Label className="text-xs font-bold uppercase tracking-wider">Resource</Label>
          <Select value={f.resource} onValueChange={(v) => setF({ ...f, resource: v })}><SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="audit-resource"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{(data?.resources || []).map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs font-bold uppercase tracking-wider">Action</Label>
          <Select value={f.action} onValueChange={(v) => setF({ ...f, action: v })}><SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="audit-action"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{(data?.actions || []).map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex gap-2"><div><Label className="text-xs font-bold uppercase tracking-wider">From</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="audit-start" /></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider">To</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="audit-end" /></div></div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-border overflow-x-auto" data-testid="audit-table">
        {!data && <p className="p-6 text-muted-foreground">Loading…</p>}
        {data?.rows.length === 0 && <p className="p-6 text-muted-foreground" data-testid="audit-empty">No audit entries match.</p>}
        {data?.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-xs uppercase tracking-wider">{["When", "User", "Action", "Resource", "Summary", "Reason", "IP"].map((h) => <th key={h} className="text-left p-3 font-bold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`audit-row-${r.id}`}>
                  <td className="p-3 whitespace-nowrap text-muted-foreground">{r.at.slice(0, 19).replace("T", " ")}</td>
                  <td className="p-3 whitespace-nowrap"><span className="font-bold">{r.user_name}</span><span className="block text-xs text-muted-foreground">{r.user_email} · {r.role}</span></td>
                  <td className="p-3"><span className="text-xs font-bold px-2.5 py-1 rounded-full bg-muted whitespace-nowrap">{r.action.replace(/_/g, " ")}</span></td>
                  <td className="p-3 whitespace-nowrap">{r.resource}</td>
                  <td className="p-3 max-w-md"><p className="font-semibold">{r.summary}</p>
                    {open === r.id && (r.before || r.after) && (
                      <pre className="mt-2 text-xs bg-muted rounded-xl p-3 whitespace-pre-wrap" data-testid={`audit-diff-${r.id}`}>{`Before: ${JSON.stringify(r.before, null, 1)}\nAfter: ${JSON.stringify(r.after, null, 1)}`}</pre>)}</td>
                  <td className="p-3 text-muted-foreground max-w-xs">{r.reason}</td>
                  <td className="p-3 text-xs text-muted-foreground">{r.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
